import React, { memo, useEffect, useState } from "react";
import ActivitiesIcons from "../common/ActivitiesIcons";
import HorizontalDatePicker from "../HorizontalDatePicker";
import AvailableActivitiesAndBooked from "../AvailableActivitiesAndBooked";
import Activity from "../Activity";
import Filters from "../Filters";
import {
  BASE_URL,
  getHelper,
  getToken,
  parseSafeDate,
  removeAccents,
  filterGuestEventsByActivityDay,
  getCalendarFetchRangeForLogicalAnchor,
  getGuestActivityDayStartMinutesFromGeneral,
} from "../../general/helper";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";

const Home = memo(({ tags, lid, type, daysToShowOnMobile, general, lang }) => {
  const { user } = useAuth();
  const guestActivityDayStartMinutes =
    getGuestActivityDayStartMinutesFromGeneral(general);

  const params = new URLSearchParams(window.location.search);
  const filteredTagsToDisplay = params.get("tags") || "";
  const todayOnlyParam = params.get("todayOnly");

  const todayOnly =
    params.has("todayOnly") &&
    (todayOnlyParam === null ||
      todayOnlyParam === "" ||
      todayOnlyParam === "true" ||
      todayOnlyParam === "1");

  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState([]);
  const [which, setWhich] = useState(undefined);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [formattedSelectedDay, setFormattedSelectedDay] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [day, setDay] = useState("");
  const [activitiesTags, setActivitiesTags] = useState([]);
  const [activities, setActivities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [clickedActivities, setClickedActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [bookIsDone, setBookIsDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const goToNow = () => {
    const params = new URLSearchParams();

    if (lid) params.set("lid", String(lid));
    if (type) params.set("type", String(type));
    if (lang) params.set("lang", String(lang));
    if (todayOnly) params.set("todayOnly", "true");

    // if it's array -> join, if it's string -> keep
    if (filteredTagsToDisplay && Array.isArray(filteredTagsToDisplay)) {
      if (filteredTagsToDisplay.length)
        params.set("tags", filteredTagsToDisplay.join(","));
    } else if (filteredTagsToDisplay) {
      params.set("tags", String(filteredTagsToDisplay));
    }

    const qs = params.toString();
    navigate(qs ? `/now?${qs}` : "/now");
  };

  const openActivityModal = (activity, which) => {
    // which could be view/book or view/delete
    setSelectedActivity(activity);
    setWhich(which);
    setIsActivityOpen(true);
  };

  const closeActivityModal = () => {
    setSelectedActivity([]);
    setIsActivityOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        setFilteredActivities([]);
        setClickedActivities([]);

        const { from: fetchFrom, to: fetchTo } =
          getCalendarFetchRangeForLogicalAnchor(
            formattedSelectedDay,
            guestActivityDayStartMinutes,
          );

        const [events, fetchBooked] = await Promise.all([
          getHelper(
            `${BASE_URL}/activitiesSchedule/guest/events?from=${fetchFrom}&to=${fetchTo}`,
            { authorization: `token ${getToken()}` },
          ),
          getHelper(
            `${BASE_URL}/activitiesSchedule/guest/bookings?from=${fetchFrom}&to=${fetchTo}`,
            { authorization: `token ${getToken()}` },
          ),
        ]);
        setLoading(false);
        const currentTime = new Date();
        const eventsList = Array.isArray(events) ? events : [];
        const eventsInLogicalDay = filterGuestEventsByActivityDay(
          eventsList,
          formattedSelectedDay,
          guestActivityDayStartMinutes,
        );

        // Filter events based on start time
        const filteredEventsByStartTimeAndTagToDisplay = eventsInLogicalDay.filter(
          (event) => {
            const endTime = parseSafeDate(event.end);
            if (filteredTagsToDisplay) {
              const hasDisplayTag = filteredTagsToDisplay
                ?.split(",")
                .some((tag) => event.activity.tags.includes(tag));
          
                if (hasDisplayTag) {
                return endTime > currentTime;
              }
              return false;
            } else {
              return endTime > currentTime;
            }
          },
        );
        setActivities(filteredEventsByStartTimeAndTagToDisplay);
        setBookings(fetchBooked);

        // Fetch tags
        const eventTags = filteredEventsByStartTimeAndTagToDisplay.flatMap(
          (e) => e.activity.tags,
        );
        const activitiesTagsByDay = tags.filter((tag) =>
          eventTags.includes(tag.id),
        );
        setActivitiesTags(activitiesTagsByDay);
      } catch (error) {
        setLoading(false);
        // Handle any errors that occurred during the API request
        // You can show an error message or redirect to an error page
      }
    };

    fetchData();
  }, [
    tags,
    formattedSelectedDay,
    bookIsDone,
    filteredTagsToDisplay,
    guestActivityDayStartMinutes,
  ]);

  const filteredActivitiesHandler = (id) => {
    setFilteredActivities([]);
    setClickedActivities([]);
    const isClicked = clickedActivities.includes(id);
    const filtered = activities.filter((activity) =>
      activity.activity.tags.includes(id),
    );
    if (isClicked) {
      setClickedActivities(
        clickedActivities.filter((activityId) => activityId !== id),
      );
    } else {
      setFilteredActivities(filtered);
      setClickedActivities([id]);
    }
  };

  return (
    <div>
      {isFiltersOpen ? (
        <Filters onCloseFilters={() => setIsFiltersOpen(false)} />
      ) : (
        <>
          <div className="flex items-start">
            {/*<button onClick={() => setIsFiltersOpen(true)} className="mx-2 w-1/6">*/}
            {/*	<FilterIcon />*/}
            {/*</button>*/}
            <div className="w-5/6">
              <div className="flex">
                {tags && activitiesTags && (
                  <ActivitiesIcons
                    handleClick={filteredActivitiesHandler}
                    clickedActivities={clickedActivities}
                    activitiesTags={activitiesTags}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="my-4">
            {!todayOnly && (<HorizontalDatePicker
              setDay={setDay}
              daysToShowOnMobile={daysToShowOnMobile}
              setFormattedSelectedDay={setFormattedSelectedDay}
              guestActivityDayStartMinutes={guestActivityDayStartMinutes}
            />)}
          </div>
          <AvailableActivitiesAndBooked
            lang={lang}
            activitiesTags={activitiesTags}
            activities={
              filteredActivities.length > 0 ? filteredActivities : activities
            }
            bookings={bookings}
            openActivityModal={openActivityModal}
            lid={lid}
            type={type}
            formattedSelectedDay={formattedSelectedDay}
            day={day}
            loading={loading}
            user={user}
          />

          {isActivityOpen && (
            <Activity
              lang={lang}
              which={which}
              setBookIsDone={() => setBookIsDone(!bookIsDone)}
              user={user}
              selectedActivity={selectedActivity}
              isActivityOpen={isActivityOpen}
              closeActivityModal={closeActivityModal}
              formattedSelectedDay={formattedSelectedDay}
              general={general}
            />
          )}
        </>
      )}
      <div className="flex justify-center items-center fixed left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 mb-12">
        {/* <Link to={`/now?lid=${lid}&type=${type}&tags=${filteredTagsToDisplay}`}> */}
        <button
          onClick={goToNow}
          className="border-2 bg-white border-black border-solid rounded-full px-4 py-2 font-semibold text-sm"
        >
          {`${removeAccents(t("whatCanIdoNow"))}`}
        </button>
        {/* </Link> */}
      </div>
    </div>
  );
});

export default Home;
